import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";

import { StyledCard } from "../common/StyledCard";
import { CurriculumRequirementResponseDto } from "../../shared/dtos/curriculum/CurriculumRequirementResponseDto";

interface CurriculumRequirementCardProps {
  requirement: CurriculumRequirementResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const CurriculumRequirementCard: React.FC<CurriculumRequirementCardProps> = ({
  requirement,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <MenuBookIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {requirement.subjectName}
              </Typography>
            </Stack>
            <Chip
              label={requirement.gradeLevelName}
              size="small"
              color="secondary"
              variant="outlined"
              icon={<SchoolIcon fontSize="small" />}
              sx={{
                mt: 1,
                height: 24,
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            />
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Requirement">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Requirement">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Details */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CalendarTodayIcon
              fontSize="small"
              sx={{ color: "action.active" }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Academic Year:</b> {requirement.academicYearName}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Chip
              label={`${requirement.lessonsPerWeek} Lessons / Week`}
              size="small"
              color="info"
              sx={{ height: 24, fontSize: "0.75rem" }}
            />
            {requirement.requiresDoubleLesson && (
              <Chip
                label="Double Lesson Req."
                size="small"
                color="warning"
                icon={<LooksTwoIcon fontSize="small" />}
                sx={{ height: 24, fontSize: "0.75rem" }}
              />
            )}
          </Stack>
        </Stack>

        {/* Branch Footer */}
        {requirement.branchName && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Branch: {requirement.branchName}
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default CurriculumRequirementCard;
